import { useCallback, useEffect, useRef, useState } from "react";
import smartMapperApis, {
  JobResponse,
  MappingStatus,
} from "../../../../../api/smartMapper/smartMapperApis";

interface UseSmartMapperJobResult {
  job: JobResponse | null;
  error: string | null;
  isPolling: boolean;
  start: (jobId: string, until?: MappingStatus[]) => void;
  stop: () => void;
  reset: () => void;
}

// Default — used by the analyse leg, where we wait for the LLM to produce
// a suggested mapping.
const DEFAULT_TERMINAL: MappingStatus[] = [
  "suggested",
  "transformed",
  "failed",
];
const POLL_INTERVAL_MS = 2500;

/**
 * Polls /smart-mapper/jobs/{jobId} every 2.5s while mapping_status is in a
 * non-terminal state. Caller passes the terminal set via `until`:
 *   - analyse leg → ["suggested", "failed"]      (default)
 *   - transform leg → ["transformed", "failed"]  (skip "confirmed", which
 *     is a transient station on the way to "transformed").
 */
export function useSmartMapperJob(): UseSmartMapperJobResult {
  const [job, setJob] = useState<JobResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isPolling, setIsPolling] = useState(false);
  const timeoutRef = useRef<number | null>(null);
  const stoppedRef = useRef(true);

  const clearTimer = () => {
    if (timeoutRef.current !== null) {
      window.clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  const stop = useCallback(() => {
    stoppedRef.current = true;
    setIsPolling(false);
    clearTimer();
  }, []);

  const reset = useCallback(() => {
    stop();
    setJob(null);
    setError(null);
  }, [stop]);

  const start = useCallback(
    (jobId: string, until: MappingStatus[] = DEFAULT_TERMINAL) => {
      stoppedRef.current = false;
      setIsPolling(true);
      setError(null);

      const tick = async () => {
        if (stoppedRef.current) return;
        try {
          const res = await smartMapperApis.getJob(jobId);
          if (stoppedRef.current) return;
          setJob(res);
          const status = res?.mapping_status;
          if (status === "failed") {
            setError(res?.error_message || "Job failed");
            setIsPolling(false);
            stoppedRef.current = true;
            return;
          }
          if (status && until.includes(status)) {
            setIsPolling(false);
            stoppedRef.current = true;
            return;
          }
          timeoutRef.current = window.setTimeout(tick, POLL_INTERVAL_MS);
        } catch (err: any) {
          if (stoppedRef.current) return;
          setError(err?.message || "Failed to fetch job status");
          setIsPolling(false);
          stoppedRef.current = true;
        }
      };

      // Kick off immediately rather than waiting for the first tick.
      void tick();
    },
    []
  );

  useEffect(() => {
    return () => {
      stoppedRef.current = true;
      clearTimer();
    };
  }, []);

  return { job, error, isPolling, start, stop, reset };
}
